import React, { useEffect, useState } from 'react';
import FormButton from 'components/form-components/FormButton';
import AlertModal from 'components/common-components/AlertModal';
import TextField from 'components/form-components/TextField';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import toast from 'react-hot-toast';
import PropTypes from 'prop-types';
import { CallAPI } from 'actions/General';
import { useTranslation } from 'react-i18next';
import Error from 'components/Error';
import ConfirmationModal from 'components/common-components/ConfirmationModal';
import { validateSqlColumnName } from 'pages/forms/utils';
import { CompanyConnectionExportSchema } from 'pages/company-setup/utils';
import refreshCCWIcon from 'assets/images/icons/refresh-ccw.svg';
import trashIcon from 'assets/images/icons/trash.svg';
import { Spinner } from 'react-bootstrap';

const EXPORT_CONFIG = {
  companyExport: {
    key: 'externalExportSetting',
    updateMsg: 'Company export setting updated successfully',
    deleteMsg: 'Company export setting deleted successfully',
    reimportMsg: 'Company export re-import started, please refresh the page after some time',
    reimportApi: 'REIMPORT_SQL_SUBMISSIONS_FOR_COMPANY',
    label: 'Company SQL',
  },
  companyFormStructureExport: {
    key: 'externalFormsStructureExportSetting',
    updateMsg: 'Company form structure export setting updated successfully',
    deleteMsg: 'Company form structure export setting deleted successfully',
    reimportMsg:
      'Company form structure export re-import started, please refresh the page after some time',
    reimportApi: 'REIMPORT_SQL_FORM_STRUCTURE_FOR_COMPANY',
    label: 'FormStructure export',
  },
  company_task_sql: {
    key: 'taskExportSetting',
    updateMsg: 'Task export setting updated successfully',
    deleteMsg: 'Company Taks setting deleted successfully',
    reimportMsg: 'Company Tasks re-import started.',
    reimportApi: 'REIMPORT_SQL_TASK_FOR_COMPANY',
    label: 'Tasks SQL',
  },
  company_attendance_sql: {
    key: 'attendanceExportSetting',
    updateMsg: 'Attendance export setting updated successfully',
    deleteMsg: 'Company Attendance setting deleted successully',
    reimportMsg: 'Company Attendance re-import started.',
    reimportApi: 'REIMPORT_SQL_ATTENDANCE_FOR_COMPANY',
    label: 'Attendance SQL',
  },
  company_user_sql: {
    key: 'usersExportSetting',
    updateMsg: 'User export setting updated successfully',
    deleteMsg: 'Company User setting deleted successully',
    reimportMsg: 'Company User re-import started.',
    reimportApi: 'REIMPORT_SQL_USERS_FOR_COMPANY',
    label: 'User SQL',
  },
};

const DatabaseExportModal = ({
  companyDetails,
  getCompanyDetail,
  exportType,
  handleClose,
  refreshList,
}) => {
  const { t } = useTranslation();

  const config = EXPORT_CONFIG[exportType];

  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingReImport, setLoadingReImport] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState('');
  const [APIError, setAPIError] = useState('');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [values, setValues] = useState({
    requestType: 'sql_db',
    externalDbTableName: '',
    existing: false,
  });

  useEffect(() => {
    const settings = companyDetails?.[config.key];

    setValues({
      externalDbTableName: settings?.externalDbTableName
        ? settings.externalDbTableName
        : exportType === 'company_attendance_sql'
        ? `company_attendance_${companyDetails.companyCode}`
        : exportType === 'company_task_sql'
        ? `company_tasks_${companyDetails.companyCode}`
        : exportType === 'company_user_sql'
        ? `company_users_${companyDetails.companyCode}`
        : '',
      requestType: settings?.requestType ?? 'sql_db',
      existing: !!settings,
    });
  }, [companyDetails]);

  const handleChange = (field, value) => {
    if (validateSqlColumnName(value)) {
      setErrors({ ...errors, [field]: undefined });
      setValues({ ...values, [field]: value });
      setTouched({ ...touched, [field]: true });
    }
  };

  const submit = async () => {
    setLoadingSave(true);
    setTouched({});
    setAPIError('');
    try {
      await CompanyConnectionExportSchema.validate(values, { abortEarly: false });
      const payload = config.key
        ? {
            [config.key]: {
              requestType: values.requestType,
              externalDbTableName: values.externalDbTableName,
            },
          }
        : {};

      let tableExist = false;
      if (!values.existing) {
        const exist = await checkIfTableNameExist(values.externalDbTableName);

        if (exist) {
          setAPIError(`Table: ${values.externalDbTableName} already exists`);
          tableExist = true;
        }
      }

      if (!tableExist) {
        const result = await CallAPI(
          'UPDATE_COMPANY_SQL_SETTINGS',
          payload,
          null,
          setLoadingSave,
          setAPIError,
        );
        if (result.status) {
          toast.success(config.updateMsg);
          handleClose();
          getCompanyDetail();
        }
      }
    } catch (error) {
      const schemaErrors = {};
      error.inner?.forEach((err) => {
        schemaErrors[err.path] = err.message.split('.').at(-1);
      });

      setErrors({ ...schemaErrors });
      setLoadingSave(false);
    }
  };

  const reImportSQLSubmissionsData = async () => {
    setLoadingReImport(true);
    setTouched({});
    setAPIError('');
    try {
      const result = await CallAPI(config.reimportApi, {}, null, setLoadingReImport, setAPIError);

      if (result.status) {
        toast.success(config.reimportMsg);
        handleClose();
      }
    } catch (error) {
      const schemaErrors = {};
      error.inner?.forEach((err) => {
        schemaErrors[err.path] = err.message.split('.').at(-1);
      });
      setErrors({ ...schemaErrors });
      setLoadingReImport(false);
    }
  };

  const checkIfTableNameExist = async (tableName) => {
    const result = await CallAPI(
      'CHECK_EXTERNAL_DB_TABLE_NAME',
      { tableName },
      null,
      setLoadingSave,
      null,
      null,
    );

    if (result.status && result.data.tableExist) return true;
    else return false;
  };

  const deleteExternalSetting = async () => {
    setLoadingDelete(true);
    setTouched({});
    setAPIError('');
    try {
      const result = await CallAPI(
        'UPDATE_COMPANY_SQL_SETTINGS',
        { [config.key]: null },
        null,
        null,
        setAPIError,
      );
      if (result.status) {
        toast.success(config.deleteMsg);

        refreshList();
        getCompanyDetail();
        handleClose();
      }
    } catch (error) {
      console.log(error);
    }
    setLoadingDelete(false);
  };

  return (
    <>
      <AlertModal show={true} actionButtons={[]} noPadding>
        <div className='w-100'>
          <div className='d-flex px-4 py-3 border-bottom-grey-10-h-1'>
            <label className='theme-size-1_5 theme-font-inter-medium theme-text-black-1'>
              {t(config.label)}
            </label>
          </div>

          {APIError && (
            <div className='mb-2'>
              <Error msg={APIError} />
            </div>
          )}

          <div className='px-4 py-3'>
            <label className='mb-2 theme-font-inter-regular theme-text-black-1'>
              {t('field_sql_table_name')}
            </label>
            <TextField
              name='externalDbTableName'
              value={values.externalDbTableName}
              placeholder={'field_sql_table_name'}
              error={errors.externalDbTableName}
              touched={touched.externalDbTableName}
              handleChange={handleChange}
              classes='mb-3'
              isHorizontal={true}
              shrinkFieldOnly={true}
              disabled={
                values.existing ||
                loadingReImport ||
                loadingSave ||
                loadingDelete ||
                exportType === 'company_attendance_sql' ||
                exportType === 'company_task_sql' ||
                exportType === 'company_user_sql'
              }
            />

            <div className='mt-3 w-100 d-flex gap-4'>
              {/* delete button */}
              {(() => {
                const loading = loadingDelete;
                const disabled = !values.existing || loadingReImport || loadingSave;
                return (
                  <div className='d-flex align-items-center gap-2 py-2'>
                    <img className={disabled ? 'svg-grey' : ''} src={trashIcon} />
                    <label
                      className={`theme-text-green-1 cursor-pointer ${
                        loading || disabled ? 'pe-none cursor-not-allowed' : ''
                      } ${disabled ? 'theme-text-grey-1' : ''}`}
                      onClick={() => setConfirmationModal('delete')}
                    >
                      {t('button_delete')}
                    </label>
                    {loading && <Spinner className='theme-text-green-1' />}
                  </div>
                );
              })()}

              {/* reImport button */}
              {(() => {
                const loading = loadingReImport;
                const disabled = !values.existing || loadingDelete || loadingSave;
                return (
                  <div className='d-flex align-items-center gap-2 py-2'>
                    <img className={disabled ? 'svg-grey' : ''} src={refreshCCWIcon} />
                    <label
                      className={`theme-text-green-1 cursor-pointer ${
                        loading || disabled ? 'pe-none cursor-not-allowed' : ''
                      } ${disabled ? 'theme-text-grey-1' : ''}`}
                      onClick={() => setConfirmationModal('reImport')}
                    >
                      {t('button_reimport')}
                    </label>
                    {loading && <Spinner className='theme-text-green-1' />}
                  </div>
                );
              })()}
            </div>
          </div>

          <div className='d-flex gap-2 justify-content-end p-3 border-top-grey-10-h-1'>
            <FormButton text='button_cancel' variant='white-1' onClick={handleClose} />
            <FormLoadingButton
              onClick={submit}
              loading={loadingSave}
              disabled={loadingReImport || loadingDelete}
              text='button_save'
              variant='green-1'
            />
          </div>
        </div>
      </AlertModal>
      <ConfirmationModal
        show={!!confirmationModal}
        title={t('alert_common_title')}
        message={t(
          confirmationModal === 'delete'
            ? 'alert_export_setting_deletion_message'
            : 'alert_export_setting_reimport_message',
        )}
        actionList={[
          {
            color: 'black-1',
            text: t('button_cancel'),
            onClick: () => setConfirmationModal(''),
          },
          {
            color: 'red-1',
            text: t(confirmationModal === 'delete' ? 'button_delete' : 'button_update'),
            onClick: () => {
              if (confirmationModal === 'delete') {
                deleteExternalSetting();
              } else {
                reImportSQLSubmissionsData();
              }
              setConfirmationModal('');
            },
          },
        ]}
      />
    </>
  );
};

DatabaseExportModal.propTypes = {
  companyDetails: PropTypes.object,
  getCompanyDetail: PropTypes.func.isRequired,
  exportType: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  refreshList: PropTypes.func.isRequired,
};

export default DatabaseExportModal;
